import React, { useState, useEffect, useRef } from "react";
import { Upload, File, Trash2, ChevronDown } from "lucide-react";
import toast from "react-hot-toast";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../firebase";

import SelectMenu from "./crm/SelectMenu";
// Expense types based on the screenshot
const EXPENSE_TYPES = [
  "None",
  "Airfare",
  "Advocate fee",
  "Auto Fare",
  "Bus Fare",
  "BankLink charges (auto assign)",
  "Conveyance (Cab)",
  "Court Fees",
  "Court Filing Expenses",
  "Consultant",
  "Conveyance (Self-car)",
  "Food",
  "FAX",
  "Hotel Expenses",
  "Metro Fare",
  "Travel to (or on behalf of) clients",
  "Online Payment",
  "Other",
  "Photocopying",
  "Train Fare",
  "Telephone",
  "Miscellaneous Fees",
  "Government Fees",
  "Court fee- Reimbursement Model",
  "Professional fee- QwickBill pro",
  "Professional fee- Bill supported/Non Standard"
];

function AddExpenseForm({ isOpen, onClose, onAddExpense, editMode = false, expenseToEdit = null, onEditExpense, isGlobalView = false, cases = [], clients = [], isClient = false }) {
  const [formData, setFormData] = useState({
    date: "",
    title: "",
    type: "",
    description: "",
    amount: "",
    tax: "",
    paymentStatus: "unpaid",
    billable: "billable",
    selectedCase: "",
    contactId: "",
    contactName: "",
    shareToClient: "no"
  });
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showCaseDropdown, setShowCaseDropdown] = useState(false);
  const [caseSearchTerm, setCaseSearchTerm] = useState("");
  const fileInputRef = useRef(null);
  const caseDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (caseDropdownRef.current && !caseDropdownRef.current.contains(event.target)) {
        setShowCaseDropdown(false);
      }
    };
    if (showCaseDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showCaseDropdown]);

  const getFilteredCases = () => {
    if (!caseSearchTerm) return cases;
    const searchLower = caseSearchTerm.toLowerCase();
    return cases.filter((caseItem) => {
      const caseText = `${caseItem.caseNo || ""} ${caseItem.petitionerName || ""} ${caseItem.respondentName || ""}`.toLowerCase();
      return caseText.includes(searchLower);
    });
  };

  const handleCaseSelect = (caseId) => {
    setFormData((prev) => ({ ...prev, selectedCase: caseId }));
    setShowCaseDropdown(false);
    setCaseSearchTerm("");
  };

  // Prefill form data when editing
  useEffect(() => {
    if (editMode && expenseToEdit) {
      setFormData({
        date: expenseToEdit.date || "",
        title: expenseToEdit.title || "",
        type: expenseToEdit.type || "",
        description: expenseToEdit.description || "",
        amount: expenseToEdit.amount?.toString() || "",
        tax: expenseToEdit.tax != null ? expenseToEdit.tax.toString() : "",
        paymentStatus: expenseToEdit.paymentStatus || "unpaid",
        billable: expenseToEdit.billable ? "billable" : "non-billable",
        selectedCase: expenseToEdit.caseId || "",
        contactId: expenseToEdit.contactId || "",
        contactName: expenseToEdit.contactName || "",
        shareToClient: expenseToEdit.shareToClient || "no"
      });
      setAttachments(expenseToEdit.attachments || []);
    } else {
      // Reset form when not editing
      setFormData({
        date: "",
        title: "",
        type: "",
        description: "",
        amount: "",
        tax: "",
        paymentStatus: "unpaid",
        billable: "billable",
        selectedCase: "",
        contactId: "",
        contactName: "",
        shareToClient: "no"
      });
      setAttachments([]);
    }
  }, [editMode, expenseToEdit, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "amount" || name === "tax") {
      if (value !== "" && !/^\d*\.?\d*$/.test(value)) return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleContactSelect = (e) => {
    const contactId = e.target.value;
    const selectedClient = clients.find((c) => c.id === contactId);
    if (selectedClient) {
      const fullName = selectedClient.salutation
        ? `${selectedClient.salutation} ${selectedClient.name}`
        : selectedClient.name;
      setFormData((prev) => ({
        ...prev,
        contactId: contactId,
        contactName: fullName,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        contactId: "",
        contactName: "",
        shareToClient: "no",
      }));
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const uploadedFiles = [];

      for (const file of files) {
        // Upload to Firebase Storage
        const timestamp = Date.now();
        const fileName = `expenses/${timestamp}_${file.name}`;
        const storageRef = ref(storage, fileName);

        await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(storageRef);

        uploadedFiles.push({
          name: file.name,
          url: downloadURL,
          type: file.type,
          size: file.size
        });
      }

      setAttachments(prev => [...prev, ...uploadedFiles]);
      toast.success(`${files.length} file(s) uploaded successfully`);
    } catch (error) {
      console.error("Error uploading files:", error);
      toast.error("Failed to upload files");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRemoveFile = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const event = { target: { files } };
      handleFileSelect(event);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validation
    if (!formData.date) {
      toast.error("Please select a date");
      return;
    }
    if (!formData.type) {
      toast.error("Please select an expense type");
      return;
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    const amountNum = parseFloat(formData.amount) || 0;
    const taxNum = parseFloat(formData.tax) || 0;

    if (editMode && expenseToEdit) {
      // Update existing expense
      const updatedExpense = {
        ...expenseToEdit,
        date: formData.date,
        title: formData.title || "",
        type: formData.type,
        description: formData.description,
        amount: amountNum,
        tax: taxNum,
        totalAmount: amountNum + taxNum,
        paymentStatus: formData.paymentStatus || "unpaid",
        billable: formData.billable === "billable",
        attachments: attachments,
        contactId: formData.contactId || "",
        contactName: formData.contactName || "",
        shareToClient: formData.shareToClient || "no",
        updatedAt: new Date().toISOString()
      };

      onEditExpense(updatedExpense);
    } else {
      // Create new expense object
      const expense = {
        id: Date.now().toString(),
        date: formData.date,
        title: formData.title || "",
        type: formData.type,
        description: formData.description,
        amount: amountNum,
        tax: taxNum,
        totalAmount: amountNum + taxNum,
        paymentStatus: formData.paymentStatus || "unpaid",
        billable: formData.billable === "billable",
        attachments: attachments,
        contactId: formData.contactId || "",
        contactName: formData.contactName || "",
        shareToClient: formData.shareToClient || "no",
        createdAt: new Date().toISOString()
      };

      if (isGlobalView) {
        onAddExpense(expense, formData.selectedCase);
      } else {
        onAddExpense(expense);
      }
    }

    // Reset form
    setFormData({
      date: "",
      title: "",
      type: "",
      description: "",
      amount: "",
      tax: "",
      paymentStatus: "unpaid",
      billable: "billable",
      selectedCase: "",
      contactId: "",
      contactName: "",
      shareToClient: "no"
    });
    setAttachments([]);

    onClose();
  };

  if (!isOpen) return null;

  const inr = (n) =>
    "₹" +
    Number(n || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  const amountNum = parseFloat(formData.amount) || 0;
  const taxNum = parseFloat(formData.tax) || 0;
  const totalNum = amountNum + taxNum;
  const selectedCaseItem = cases.find((c) => c.id === formData.selectedCase);
  const labelCls = "block text-sm font-medium text-slate-600 mb-1.5";
  const fieldCls =
    "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/40";

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 pt-10">
      <form
        onSubmit={handleSubmit}
        className="my-6 w-full max-w-4xl rounded-2xl bg-white shadow-2xl"
      >
        {/* Header */}
        <div className="flex flex-col gap-3 border-b border-slate-200 p-5 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              <span>Expenses</span>
              <ChevronDown className="h-3 w-3 -rotate-90" />
              <span className="font-medium text-slate-700">
                {editMode ? "Edit Expense" : "Add Expense"}
              </span>
            </div>
            <h3 className="mt-1 text-xl font-bold text-slate-900">
              {editMode ? "Edit Expense" : "Add Expense"}
            </h3>
            <p className="mt-0.5 text-xs text-slate-500">
              Record your expense and attach it to a case and client
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading}
              className="rounded-lg bg-yellow-500 px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm transition-colors hover:bg-yellow-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {uploading ? "Uploading..." : "Save Expense"}
            </button>
          </div>
        </div>

        {/* Body: form (left) + summary (right) */}
        <div className="grid grid-cols-1 gap-6 p-5 lg:grid-cols-3">
          {/* ===== Left: form ===== */}
          <div className="space-y-4 lg:col-span-2">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {/* Related Case */}
              {isGlobalView && (
                <div>
                  <label className={labelCls}>Related Case</label>
                  <div className="relative" ref={caseDropdownRef}>
                    <button
                      type="button"
                      onClick={() => !editMode && setShowCaseDropdown(!showCaseDropdown)}
                      disabled={editMode}
                      className={`${fieldCls} flex items-center justify-between text-left disabled:opacity-60 disabled:cursor-not-allowed`}
                    >
                      <span className={`truncate ${!selectedCaseItem ? "text-slate-400" : ""}`}>
                        {selectedCaseItem
                          ? `${selectedCaseItem.petitionerName} vs ${selectedCaseItem.respondentName} (${selectedCaseItem.caseNo})`
                          : "Select a case (optional)"}
                      </span>
                      <ChevronDown
                        className={`ml-2 flex-shrink-0 text-slate-400 transition-transform ${showCaseDropdown ? "rotate-180" : ""}`}
                        size={18}
                      />
                    </button>
                    {showCaseDropdown && !editMode && (
                      <div className="absolute z-50 mt-2 flex max-h-64 w-full flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl">
                        <div className="border-b border-slate-100 p-2">
                          <input
                            type="text"
                            value={caseSearchTerm}
                            onChange={(e) => setCaseSearchTerm(e.target.value)}
                            placeholder="Search cases..."
                            className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-yellow-400 focus:outline-none"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        <div className="max-h-52 overflow-y-auto">
                          <button
                            type="button"
                            onClick={() => handleCaseSelect("")}
                            className="w-full border-b border-slate-100 px-4 py-2.5 text-left text-sm text-slate-400 hover:bg-slate-50"
                          >
                            Select a case (optional)
                          </button>
                          {getFilteredCases().length === 0 ? (
                            <div className="px-4 py-3 text-sm text-slate-400">No cases found</div>
                          ) : (
                            getFilteredCases().map((caseItem) => (
                              <button
                                key={caseItem.id}
                                type="button"
                                onClick={() => handleCaseSelect(caseItem.id)}
                                className="w-full border-b border-slate-100 px-4 py-2.5 text-left text-sm text-slate-700 last:border-b-0 hover:bg-slate-50"
                              >
                                <span className="line-clamp-2">
                                  {caseItem.petitionerName} vs {caseItem.respondentName} ({caseItem.caseNo})
                                </span>
                              </button>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Client */}
              {!isClient && (
                <div>
                  <label className={labelCls}>Client</label>
                  {clients.length === 0 ? (
                    <p className="text-sm text-slate-500">
                      No clients found. Add from{" "}
                      <span className="text-blue-600">My Clients</span>.
                    </p>
                  ) : (
                    <SelectMenu
                      name="contactId"
                      value={formData.contactId}
                      onChange={handleContactSelect}
                      className={fieldCls}
                    >
                      <option value="">Select a client (optional)</option>
                      {clients.map((client) => (
                        <option key={client.id} value={client.id}>
                          {client.salutation ? `${client.salutation} ` : ""}
                          {client.name}
                          {client.company ? ` (${client.company})` : ""}
                        </option>
                      ))}
                    </SelectMenu>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {/* Expense Title */}
              <div>
                <label className={labelCls}>Expense Title</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="e.g. Court fee - Delhi High Court"
                  className={fieldCls}
                />
              </div>
              {/* Expense Category (type) */}
              <div>
                <label className={labelCls}>
                  Expense Category <span className="text-red-500">*</span>
                </label>
                <SelectMenu
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  className={fieldCls}
                  required
                >
                  <option value="">Select category</option>
                  {EXPENSE_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </SelectMenu>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {/* Amount */}
              <div>
                <label className={labelCls}>
                  Amount (₹) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  placeholder="0.00"
                  className={fieldCls}
                  required
                />
              </div>
              {/* Tax */}
              <div>
                <label className={labelCls}>Tax (₹)</label>
                <input
                  type="text"
                  inputMode="decimal"
                  name="tax"
                  value={formData.tax}
                  onChange={handleChange}
                  placeholder="0.00"
                  className={fieldCls}
                />
              </div>
              {/* Date */}
              <div>
                <label className={labelCls}>
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  className={`${fieldCls} [color-scheme:light]`}
                  required
                />
              </div>
            </div>

            {/* Total */}
            <div className="flex items-center justify-between rounded-lg bg-slate-50 px-4 py-2.5 text-sm">
              <span className="font-medium text-slate-600">Total Amount (₹)</span>
              <span className="font-bold text-blue-600">{inr(totalNum)}</span>
            </div>

            {/* Upload Receipt */}
            <div>
              <label className={labelCls}>Upload Receipt</label>
              <div
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                className="cursor-pointer rounded-lg border-2 border-dashed border-slate-300 p-5 text-center transition-colors hover:border-yellow-400"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="mx-auto mb-2 text-slate-400" size={26} />
                <p className="text-sm text-slate-500">
                  Drop files here or{" "}
                  <span className="font-medium text-blue-600">Click to Upload</span>
                </p>
                <p className="mt-0.5 text-xs text-slate-400">Images & documents</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,.pdf,.doc,.docx"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>
              {attachments.length > 0 && (
                <div className="mt-3 space-y-2">
                  {attachments.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"
                    >
                      <div className="flex min-w-0 items-center gap-2">
                        <File size={16} className="shrink-0 text-blue-500" />
                        <span className="truncate text-sm text-slate-700">{file.name}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveFile(index)}
                        className="text-slate-400 transition-colors hover:text-red-600"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Notes */}
            <div>
              <label className={labelCls}>Notes</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Add a note (optional)"
                rows="3"
                className={`${fieldCls} resize-none`}
              />
            </div>

            {/* Share with client */}
            {!isClient && formData.contactId && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <label className="mb-2 block text-sm font-medium text-slate-600">
                  Share with Client
                </label>
                <div className="flex gap-6">
                  {["yes", "no"].map((v) => (
                    <label key={v} className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                      <input
                        type="radio"
                        name="shareToClient"
                        value={v}
                        checked={formData.shareToClient === v}
                        onChange={handleChange}
                        className="h-4 w-4 accent-yellow-500"
                      />
                      {v === "yes" ? "Yes" : "No"}
                    </label>
                  ))}
                </div>
                {formData.shareToClient === "yes" && (
                  <p className="mt-2 text-xs text-emerald-600">
                    Visible to {formData.contactName} on their dashboard.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* ===== Right: Expense Summary ===== */}
          <div className="lg:col-span-1">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
              <h4 className="text-base font-bold text-slate-900">Expense Summary</h4>
              <div className="mt-4 space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Amount</span>
                  <span className="font-medium text-slate-800">{inr(amountNum)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Tax</span>
                  <span className="font-medium text-slate-800">{inr(taxNum)}</span>
                </div>
                <div className="flex items-center justify-between border-t border-slate-200 pt-3">
                  <span className="font-semibold text-slate-700">Total Amount</span>
                  <span className="font-bold text-slate-900">{inr(totalNum)}</span>
                </div>

                {/* Billable toggle */}
                <div className="flex items-center justify-between border-t border-slate-200 pt-3">
                  <span className="text-slate-500">Billable</span>
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((p) => ({
                        ...p,
                        billable: p.billable === "billable" ? "non-billable" : "billable",
                      }))
                    }
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                      formData.billable === "billable" ? "bg-blue-600" : "bg-slate-300"
                    }`}
                    aria-label="Toggle billable"
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                        formData.billable === "billable" ? "translate-x-4" : "translate-x-0.5"
                      }`}
                    />
                  </button>
                </div>

                {/* Invoice (static — no invoice link yet) */}
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Invoice</span>
                  <span className="text-slate-500">Not Invoiced</span>
                </div>

                {/* Payment Status */}
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Payment Status</span>
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((p) => ({
                        ...p,
                        paymentStatus: p.paymentStatus === "paid" ? "unpaid" : "paid",
                      }))
                    }
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset transition-colors ${
                      formData.paymentStatus === "paid"
                        ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                        : "bg-amber-50 text-amber-700 ring-amber-200"
                    }`}
                  >
                    {formData.paymentStatus === "paid" ? "Paid" : "Unpaid"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default AddExpenseForm;
